import { getCurrentLanguage } from '@eureka/language-tool';
import { addTrialService } from '@eureka/services/src/add-trial-service';
import { isAnonymousError } from '@eureka/services/src/exception';
import { accountDeferred, prepareTokenDeferred, setPrepareTokenDeferred } from '@eureka/utils/account';
import { isInternationalSite } from '@eureka/utils/environment';
import { HeaderService, SLD, Site } from '@patsnap-utils/biz';
import { handleLogout } from '@patsnap-utils/passport';
import psHttp, { generateExceptionRedirector, psAxios } from '@patsnap/common-ps-http';
import { createUniformSetting } from '@patsnap/uniform-setting';
import type { T_Locale, T_Region } from '@patsnap/uniform-setting/dist/locale/types';
import type { AxiosInstance } from 'axios';
import { includes, filter } from 'lodash-es';
import { customProductsLinkInUserCenter } from './custom-links';
import { type IProjectPreset, _passportDeferred, passportInterceptManager, prepareTokenBase, updateTokenBaseRequestHeaders } from './enable-token-base';
import { removeEurekaLoginCookie, setEurekaLoginCookie } from './set-eureka-login-cookie';
/**
 * 编译时常量声明
 * 这些常量会在构建时被 Vite 的 define 配置替换为实际值
 */
declare const __ENABLE_SENTRY__: boolean | undefined;
declare const __ENABLE_BACKOFFICE__: boolean | undefined;
declare const __ENABLE_ARMS__: boolean | undefined;
// declare const __ENABLE_FEISHU_CLIENT__: boolean | undefined;

const supports: T_Locale[] = ['cn', 'en', 'jp', 'tw']
export interface IStartupOption {
    headerService?: HeaderService
    projectName: string
    preset?: IProjectPreset
    pageInit: () => Promise<unknown>
    /**
     * 禁用自动赋权
     */
    disableTrial?: boolean
}

const lang = getCurrentLanguage()

/**
 * 游客模式跳转处理
 * 当用户未登录进入游客模式时,跳转到指定路由
 * - 国内站点：如果访问 guest 路由，跳转到 home；否则保持当前路由
 * - 国外站点（URL包含patsnap）：跳转到 /guest
 */
function handleGuestModeRedirect() {
    const currentHash = window.location.hash;
    const guestRoute = '#/guest';
    const homeRoute = '#/home';

    // 提取路径部分（不包含查询参数）
    const hashPath = currentHash.split('?')[0];

    // 国内站点：如果访问 guest 路由，跳转到 home
    if (!isInternationalSite()) {
        if (hashPath === guestRoute || hashPath.startsWith(`${guestRoute}/`)) {
            // 提取查询参数
            const queryString = currentHash.includes('?') ? currentHash.split('?')[1] : '';
            window.location.hash = queryString ? `${homeRoute}?${queryString}` : homeRoute;
        }
        return;
    }

    // 国外站点：如果当前不在游客路由或其子路由，则跳转（保留查询参数）
    if (hashPath !== guestRoute && !hashPath.startsWith(`${guestRoute}/`)) {
        // 提取查询参数
        const queryString = currentHash.includes('?') ? currentHash.split('?')[1] : '';
        window.location.hash = queryString ? `${guestRoute}?${queryString}` : guestRoute;
    }
}

export function startup(startupOption: IStartupOption): Promise<unknown[]> {
    const promise = startupOption.preset
        ? prepareTokenBase(startupOption.preset).then((p) => {
            setEurekaLoginCookie();
            setPrepareTokenDeferred(p)
            /* send auth ready signal */
            return p;
        }).catch(e => {
            if (isAnonymousError(e)) {
                // 只有在startup的preset.preRedirectLogin中手动throw new AnonymousError才会触发
                passportInterceptManager.eject();
                accountDeferred.reject(e);
                prepareTokenDeferred.reject(e);
                _passportDeferred.reject(e);

                // 进入游客模式时跳转到指定路由
                handleGuestModeRedirect();
            }
        })
        : Promise.resolve()
    // 仅在 stage 环境启用 ARMS 监控，使用编译时常量实现 Tree Shaking
    if (__ENABLE_ARMS__) {
        import(/* webpackChunkName: "arms" */'./arms').then(({ initArmsRum }) => {
            initArmsRum()
        })
    };

    // @ts-ignore
    return promise.then((p) => {
        const headerService = startupOption.headerService || new HeaderService()
        headerService.logoHref = new Site({ sld: SLD.EUREKA, pathname: 'home' }).href

        return Promise.all([
            startupOption.pageInit().catch(e => {
                if (!addTrialService.ENABLE_AUTO_ADD_TRIAL) return Promise.reject(e)
            }),
            headerService.sync({ http: psHttp, uri: process.env.VUE_APP_ACCOUNT_SERVICE_URI as string }).finally(
                () => {
                    customProductsLinkInUserCenter(headerService)
                    headerService.account && accountDeferred.resolve(headerService.account);
                }
            ).then(() => {
                if (startupOption.disableTrial) {
                    return;
                }
                return addTrialService.addTrial(headerService, p)
            }),
            (() => {
                const setting = createUniformSetting({
                    verbose: true,
                    axiosInstance: psAxios as AxiosInstance as any,
                    loggedIn: true,
                    baseAccountUrl: process.env.VUE_APP_ACCOUNT_SERVICE_URI,
                    locale: {
                        supports,
                    },
                    region: process.env.VUE_APP_DEPLOY_ENV as T_Region,
                })

                // @ts-ignore
                return setting.locale.getLocale().then((data) => {
                    const locale = data.user || data.default
                    updateTokenBaseRequestHeaders({ 'X-Site-Lang': locale.toUpperCase() })
                    return { locale, setting }
                })
            })()
        ]).then((data) => {
            const [pageData, , { locale, setting }] = data

            /* 如果 cookies 中存储的语言和持久化的语言不匹配，则刷新页面 */
            if (lang !== locale) {
                window.location.reload()
                return
            }

            /* WhiteLabel 时，隐藏 PatSnap Logo */
            if (headerService.whiteLabel) {
                headerService.logoVisible = false
            }

            const { locales } = headerService
            headerService.locales = filter(locales, locale => includes(supports, locale.key)).map(({ ...rest }) => {
                return {
                    ...rest,
                    active: rest.key === locale,
                }
            })

            /* 注册切换语言函数 */
            // eslint-disable-next-line @typescript-eslint/ban-types
            headerService.beforeLocaleChange = function beforeLocaleChange(localeKey: string, done: Function) {
                setting.locale.setLocale(localeKey as T_Locale).then(() => {
                    window.location.reload()
                    done()
                })
            }

            /* 注册登出回调函数 */
            // eslint-disable-next-line @typescript-eslint/ban-types
            headerService.userCenterService.logoutHandler = async function logout(_done: Function, _logoutHref: string, _option: unknown) {
                removeEurekaLoginCookie();

                // 调用 logout API
                handleLogout().then(() => {
                    window.location.reload();
                })
            }

            /** header头切换租户成功后页面跳转 */
            headerService.switchTenantHandler = async(tenantId, done) => {
                return done().then(() => {
                    window.location.href = new Site({ sld: SLD.EUREKA, pathname: 'ip/#/home' }).href
                    window.location.reload()
                })
            }

            // 通过编译时常量实现 Tree Shaking，仅 home + CN 环境打包飞书客户端检测
            // if (__ENABLE_FEISHU_CLIENT__) {
            //     import('./feishu-sdk').then(({ clientDeferred }) => {
            //         clientDeferred.promise.then(({ isFeiShu }) => {
            //             headerService.visibleProducts = !isFeiShu
            //         })
            //     })
            // } else {
            //     // 非飞书环境，直接显示产品切换
            //     headerService.visibleProducts = true
            // }

            // 用户登录后加载额外服务
            accountDeferred.promise.then(account => {
                if (account?.isLogin) {
                    // 通过编译时常量实现 Tree Shaking，国内环境下完全不打包 defer.js
                    if (__ENABLE_SENTRY__ || __ENABLE_BACKOFFICE__) {
                        import(/* webpackChunkName: "defer" */'./defer').then(({ defer }) => {
                            defer(account, locale)
                        })
                    }
                    if (__ENABLE_ARMS__) {
                        import(/* webpackChunkName: "arms" */'./arms').then(({ setArmsUserInfo }) => {
                            setArmsUserInfo(account)
                        })
                    }
                }
            })

            return [headerService, locale, pageData, setting]
        })
            .catch((error: Error) => {
                if (!addTrialService.ENABLE_AUTO_ADD_TRIAL) {
                    generateExceptionRedirector(403, () => {
                        window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href
                    })(error);
                }
            });
    })
}
